import React, {Suspense} from 'react';

import {Box, Stack, Typography} from '@mui/material';
import {AppointmentBookingEnum, BookingProcessSuccessEnum} from 'main/analytics/gaTagsEnum';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {ChangeFlowVariantEnum} from 'presentation/modules/booking/shared/ui/components/ChangeFlowDialog/constants';
import {ProviderAddressField} from 'presentation/modules/booking/shared/ui/components/ProviderAddressField';
import {ProviderAddressSkeleton} from 'presentation/modules/booking/shared/ui/components/ProviderAddressField/ProviderAdressSkeleton';
import {ReviewCard} from 'presentation/modules/booking/shared/ui/components/ReviewCard';
import {SquareLabelTime} from 'presentation/modules/booking/shared/ui/components/SquareLabelTime';
import {translations} from 'presentation/translations-new';
import {formatText} from 'presentation/utils';
import {ExamSummarySectionGroup} from '../../../../components/ExamSummarySectionGroup';
import {ExamConfirmBookingButton} from './components/ExamConfirmBookingButton';
import {useExamReviewBooking} from './hooks/useExamReviewBooking';
import {ExamReviewBookingProps} from './types';

const {appointmentSummary} = translations['pt-br'].privateFlow.bookingFlow;
const {professionalAndDate, exam} = translations['pt-br'].commons;
const {appointmentCity} = translations['pt-br'].privateFlow.profile;

export function ExamReviewBooking({handlePrevStep, stepLabels}: ExamReviewBookingProps) {
	const {
		isRebooking,
		formattedDateAndHour,
		iconName,
		isLaptop,
		isMobile,
		isTablet,
		handleChangeModals,
		activeBookingFlowStep,
		bookingData,
		day,
		month,
	} = useExamReviewBooking();

	return (
		<Stack
			gap={2}
			m={0}
			data-tag-id={BookingProcessSuccessEnum.ACESSOU_ETAPA_REVISAO_AGENDAMENTO}>
			<Typography variant="subtitle2" color="text.secondary">
				{appointmentSummary}
			</Typography>
			<ReviewCard.Root>
				<ReviewCard.Header
					handleAction={handlePrevStep}
					subheader={professionalAndDate}
					actionDataTagId={AppointmentBookingEnum.CLICK_ALTERAR_INFORMACOES_AGENDAMENTO}
				/>
				<Stack flexDirection={'row'} gap={2} alignItems={'center'}>
					<SquareLabelTime day={day} month={month} />
					<Stack>
						<Typography variant="subtitle2" fontSize={'1.125rem'}>
							{formattedDateAndHour}
						</Typography>
						<ReviewCard.Content
							iconName="medical-services-filled"
							primaryText={bookingData.professionalName}
						/>
					</Stack>
				</Stack>
			</ReviewCard.Root>

			<ReviewCard.Root>
				<ReviewCard.Header
					handleAction={
						isRebooking
							? undefined
							: () => handleChangeModals(ChangeFlowVariantEnum.SPECIALTY)
					}
					subheader={exam}
					actionDataTagId={AppointmentBookingEnum.CLICK_ALTERAR_INFORMACOES_AGENDAMENTO}
				/>
				<ReviewCard.Content
					iconName={iconName}
					primaryText={bookingData.specialtyName}></ReviewCard.Content>
			</ReviewCard.Root>

			<Box display={'grid'} gridTemplateColumns={isTablet ? '1fr' : '1fr 1fr'} gap={2}>
				<Suspense fallback={<ProviderAddressSkeleton />}>
					<ProviderAddressField flowType={FlowEnum.EXAM} />
				</Suspense>

				<ReviewCard.Root>
					<ReviewCard.Header
						handleAction={
							isRebooking
								? undefined
								: () => handleChangeModals(ChangeFlowVariantEnum.LOCATION)
						}
						subheader={appointmentCity}
						actionDataTagId={
							AppointmentBookingEnum.CLICK_ALTERAR_INFORMACOES_AGENDAMENTO
						}
					/>
					<ReviewCard.Content
						iconName={'my-location-filled'}
						primaryText={formatText(bookingData.cityName)}
						secondaryText={formatText(bookingData.ufName)}></ReviewCard.Content>
				</ReviewCard.Root>
			</Box>

			<Stack gap={2}>
				{isLaptop ? (
					<ExamSummarySectionGroup
						noBorderRadius={isMobile}
						showTitle={false}
						activeStep={activeBookingFlowStep}
						handleBack={handlePrevStep}
						stepLabels={stepLabels}
						handleChangeModals={handleChangeModals}
					/>
				) : null}

				<ExamConfirmBookingButton handleBack={handlePrevStep} />
			</Stack>
		</Stack>
	);
}
